/**
 * Public G1 self-signup flow (individual-farmer registration).
 *
 * Four steps, mirroring the mobile signup screens + the sibling forgot-pin.tsx:
 *   0. enter phone        -> POST /auth/otp/request {purpose:'signup'}
 *   1. enter 5-digit OTP  -> POST /auth/otp/verify  {purpose:'signup'}
 *   2. read + accept POPIA consent (GET /consent/current; no API call to advance)
 *   3. enter profile + PIN-> POST /auth/signup       -> AuthTokens
 *
 * On the final success the server mints tokens (same shape login returns); we
 * persist them exactly like login.tsx and route by role (a fresh signup is the
 * 'farmer' role -> /home).
 *
 * In-page copy goes through i18next (TD-088) — keys live under signup.* in
 * apps/web/src/i18n/locales/*.json; only en.json carries real English, the
 * other locales hold English placeholders until SME translations land (TD-039).
 *
 * Contracts verified against services/api/src/auth/dto.ts (exact request fields:
 * display_name, preferred_lang, consent_version_id) + consent/dto.ts.
 */
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  ColorModeToggle,
  Container,
  FormControl,
  Icon,
  InputLabel,
  LanguageRow,
  MenuItem,
  OtpInput,
  PhoneInput,
  Stepper,
  Select,
  Stack,
  Text,
  TextField,
  Typography,
  mdSysColor,
  useColorMode,
} from '@fse/ui';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseMarkWeb } from './FseMarkWeb';
import { FseApiError, readCurrentRole } from '../api/client';
import { consentControllerGetCurrent } from '../api/generated/consent/consent';
import {
  authControllerRequestOtp,
  authControllerVerifyOtp,
  authControllerSignup,
} from '../api/generated/auth/auth';
import type {
  ConsentVersionDto,
  SignupRequestPreferredLang as PreferredLang,
} from '../api/generated/models';

// Schemas are built per-render from t() so their validation messages follow the
// chosen language (TD-088). Memoised on t so the object identity is stable.
function buildSchemas(t: TFunction) {
  // Phone is captured as the 9-digit ZA-local portion (PhoneInput renders the
  // fixed `+27` adornment). E.164 is rebuilt at the API boundary, so the request
  // payload is unchanged. The same i18n message key is preserved.
  const phoneSchema = z.object({
    phone: z.string().regex(/^\d{9}$/, t('signup.validation.phone')),
  });

  const otpSchema = z.object({
    code: z.string().regex(/^\d{5}$/, t('signup.validation.otp')),
  });

  const profileSchema = z
    .object({
      displayName: z
        .string()
        .trim()
        .min(1, t('signup.validation.displayNameRequired'))
        .max(200, t('signup.validation.displayNameTooLong')),
      pin: z.string().regex(/^\d{4,8}$/, t('signup.validation.pin')),
      confirmPin: z.string(),
    })
    .refine((v) => v.pin === v.confirmPin, {
      path: ['confirmPin'],
      message: t('signup.validation.pinMismatch'),
    });

  return { phoneSchema, otpSchema, profileSchema };
}

const SUPPORTED_LANGUAGES: readonly PreferredLang[] = ['en', 'xi', 'nso', 'ts'];

// Language self-labels (endonyms) — intentionally NOT translated; each label is
// the language's own name in its own script.
const LANGUAGE_LABELS: Record<PreferredLang, string> = {
  en: 'English',
  xi: 'isiXhosa',
  nso: 'Sepedi',
  ts: 'Xitsonga',
};

// Native (endonym) + English label per language, for the Welcome-step language
// tile grid (LanguageRow shows native large, English as the subtitle). Labels
// are locale-agnostic per FSE content rules so a speaker of any of the four
// languages can read their own name.
const LANGUAGE_META: Record<PreferredLang, { native: string; english: string }> = {
  en: { native: 'English', english: 'English' },
  xi: { native: 'isiXhosa', english: 'Xhosa' },
  nso: { native: 'Sepedi', english: 'Northern Sotho' },
  ts: { native: 'Xitsonga', english: 'Tsonga' },
};

function persistTokens(tokens: { access_token: string; refresh_token: string }) {
  window.localStorage.setItem('fse_access_token', tokens.access_token);
  window.localStorage.setItem('fse_refresh_token', tokens.refresh_token);
}

// Typed handle on the /signup route so `search.ref` is read against the route's
// validateSearch (router.ts), which already UUID-guards the param. `ref` here is
// therefore always a valid UUID string or undefined.
const signupRouteApi = getRouteApi('/signup');

// Shared with the web i18n bootstrap (apps/web/src/i18n/index.ts) + Settings
// picker — persists the Welcome-step language choice so it survives a reload.
const LANGUAGE_KEY = 'fse_lang';

export function SignupScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Mode-aware MD3 system colours for the Welcome hero band — using the role
  // tokens directly (primaryContainer / onPrimaryContainer) guarantees correct
  // contrast in BOTH light and dark, unlike MUI's primary.light/contrastText.
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];

  // Referral attribution (TD-097) — the `?ref=<userId>` query param from an
  // "invite a farmer" share link. Validated/UUID-guarded at the route boundary;
  // passed through to POST /auth/signup as `referrerId`. Absent/invalid → the
  // field is omitted from the payload (the server silently ignores unknowns too).
  const { ref: referrerId } = signupRouteApi.useSearch();

  // Wizard step labels (resolved per-render so they follow the language).
  const STEPS = [
    t('signup.step.phone'),
    t('signup.step.verify'),
    t('signup.step.terms'),
    t('signup.step.profile'),
  ] as const;

  const { phoneSchema, otpSchema, profileSchema } = useMemo(() => buildSchemas(t), [t]);

  // Step machine. 'welcome' is the first screen (language pick + brand hero);
  // the numbered 0..3 steps are the original wizard (phone / verify / terms /
  // profile) and keep their numeric identities so the Stepper + consent effect
  // (step === 2) are unchanged.
  const [step, setStep] = useState<'welcome' | 0 | 1 | 2 | 3>('welcome');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [preferredLang, setPreferredLang] = useState<PreferredLang>('en');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  // Consent (step 2): fetched on entering the step. `consent` holds the version
  // (its id flows into the signup payload); `accepted` gates the continue button.
  const [consent, setConsent] = useState<ConsentVersionDto | null>(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentReload, setConsentReload] = useState(0);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<{
    phone?: string;
    code?: string;
    displayName?: string;
    pin?: string;
    confirmPin?: string;
  }>({});
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // OTP resend countdown (design fidelity): a local timer that starts at
  // RESEND_SECONDS each time a code is sent. While it counts down the resend
  // action is replaced by "Resend code in 0:NN"; at zero a "Resend code" button
  // re-calls the EXISTING /auth/otp/request endpoint and restarts the timer.
  const RESEND_SECONDS = 30;
  const [resendSeconds, setResendSeconds] = useState(0);
  const [resending, setResending] = useState(false);

  // Fetch the current consent whenever we land on step 2 (and on Retry).
  useEffect(() => {
    if (step !== 2) return;
    const controller = new AbortController();
    setConsentLoading(true);
    setConsentError(null);
    consentControllerGetCurrent(controller.signal)
      .then((dto) => {
        setConsent(dto);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setConsent(null);
        setConsentError(
          err instanceof FseApiError ? err.message : t('signup.consent.loadError'),
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setConsentLoading(false);
      });
    return () => controller.abort();
  }, [step, consentReload]);

  // Tick the resend countdown once per second while it's above zero. A single
  // setTimeout per render (re-armed by the dependency on resendSeconds) — the
  // local-timer approach the design specifies, no extra deps.
  useEffect(() => {
    if (resendSeconds <= 0) return;
    const id = window.setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendSeconds]);

  // Re-request the OTP via the existing endpoint, then restart the countdown.
  async function onResendOtp() {
    if (resendSeconds > 0 || resending) return;
    setApiErrorMessage(null);
    setResending(true);
    try {
      await authControllerRequestOtp({ phone: `+27${phone}`, purpose: 'signup' });
      setResendSeconds(RESEND_SECONDS);
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'request'));
    } finally {
      setResending(false);
    }
  }

  function mapApiError(err: unknown, kind: 'request' | 'verify' | 'signup'): string {
    if (err instanceof FseApiError) {
      if (err.status === 409) {
        return t('signup.apiError.conflict');
      }
      if (err.status === 404) {
        return t('signup.apiError.notFound');
      }
      if (err.status === 401) {
        return kind === 'signup'
          ? t('signup.apiError.expired')
          : t('signup.apiError.badCode');
      }
      if (err.status === 400) {
        return t('signup.apiError.badRequest');
      }
      return err.message;
    }
    return String(err);
  }

  async function onSubmitPhone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = phoneSchema.safeParse({ phone });
    if (!parsed.success) {
      setFieldErrors({ phone: parsed.error.issues[0]?.message });
      return;
    }
    setPending(true);
    try {
      // Generated orval client (TD-070) + the E.164 rebuilt from the 9 validated
      // local digits of the PhoneInput (design migration, TD-089).
      await authControllerRequestOtp({ phone: `+27${parsed.data.phone}`, purpose: 'signup' });
      setResendSeconds(RESEND_SECONDS);
      setStep(1);
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'request'));
    } finally {
      setPending(false);
    }
  }

  async function onSubmitOtp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = otpSchema.safeParse({ code });
    if (!parsed.success) {
      setFieldErrors({ code: parsed.error.issues[0]?.message });
      return;
    }
    setPending(true);
    try {
      await authControllerVerifyOtp({ phone: `+27${phone}`, code: parsed.data.code, purpose: 'signup' });
      setStep(2);
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'verify'));
    } finally {
      setPending(false);
    }
  }

  function onAcceptConsent() {
    if (!consent) return;
    setApiErrorMessage(null);
    setStep(3);
  }

  // The "scroll to read" gate (below) flips `scrolledToBottom` via the box's
  // onScroll handler — but a short consent body that fits inside the box never
  // overflows, so no scroll event ever fires and the Accept button would stay
  // disabled forever. Measure on mount: if there's nothing to scroll, the
  // notice is already fully visible, so treat it as read.
  const consentBoxRef = useCallback((node: HTMLDivElement | null) => {
    if (node && node.scrollHeight <= node.clientHeight + 8) {
      setScrolledToBottom(true);
    }
  }, []);

  async function onSubmitProfile(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = profileSchema.safeParse({ displayName, pin, confirmPin });
    if (!parsed.success) {
      const errs: { displayName?: string; pin?: string; confirmPin?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === 'displayName' || key === 'pin' || key === 'confirmPin') {
          errs[key] = issue.message;
        }
      }
      setFieldErrors(errs);
      return;
    }
    if (!consent) {
      setApiErrorMessage(t('signup.apiError.expired'));
      return;
    }
    setPending(true);
    try {
      const tokens = await authControllerSignup({
        phone: `+27${phone}`,
        pin: parsed.data.pin,
        display_name: parsed.data.displayName,
        preferred_lang: preferredLang,
        consent_version_id: consent.id,
        // Only sent when a valid referral rode in on the URL; omitted otherwise
        // so the optional field is absent on the wire (TD-097).
        referrerId: referrerId ?? undefined,
      });
      persistTokens(tokens);
      const role = readCurrentRole();
      if (role === 'farmer') {
        void navigate({ to: '/home' });
      } else if (role === 'sysCollab') {
        window.location.assign('/kanban.html');
      } else {
        void navigate({ to: '/dashboard' });
      }
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'signup'));
    } finally {
      setPending(false);
    }
  }

  // MUI's Select fires a SelectChangeEvent that isn't re-exported through
  // @fse/ui; we only read target.value, so a minimal structural type keeps us
  // within the allowed surface (mirrors _app.profile.tsx).
  function onLangChange(event: { target: { value: string } }) {
    setPreferredLang(event.target.value as PreferredLang);
  }

  // Welcome-step language pick: switch the live UI language, persist the choice
  // (so the rest of the wizard + a reload follow it), and seed `preferredLang`
  // so it rides through to the signup payload (the profile step still lets the
  // user change it). i18n.changeLanguage returns a promise we don't need to
  // await — the React tree re-renders on the languageChanged event.
  function onPickLanguage(lang: PreferredLang) {
    setPreferredLang(lang);
    void i18n.changeLanguage(lang);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_KEY, lang);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ position: 'fixed', top: 8, right: 8 }}>
        <ColorModeToggle />
      </Box>
      <Container maxWidth="xs">
        <Stack spacing={3}>
          {step === 'welcome' ? (
            <>
              {/* Hero band — primary/tertiary-container brand banner with the
                  FseMark, display headline + tagline. Tokens (not hex) so the
                  band recolours in dark mode. */}
              <Box
                sx={{
                  bgcolor: sys.primaryContainer,
                  color: sys.onPrimaryContainer,
                  borderRadius: 4,
                  px: 3,
                  py: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 1.5,
                }}
              >
                <FseMarkWeb size={80} />
                <Text variant="display-small" as="h1" align="center">
                  {t('signup.welcome.title', 'Have FSE.')}
                </Text>
                <Box
                  sx={{
                    opacity: 0.9,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  <Text variant="label-large" align="center">
                    {t('signup.welcome.brand', 'Food Security Exchange')}
                  </Text>
                </Box>
                <Text variant="body-medium" align="center">
                  {t(
                    'signup.welcome.tagline',
                    'List your crop. Get verified buyers. Paid same day, every time.',
                  )}
                </Text>
              </Box>

              {/* Language choice — 2-up tile grid of LanguageRow (native script
                  first). Picking switches the live UI language + seeds the
                  signup preferred-language. */}
              <Box>
                <Box sx={{ mb: 1 }}>
                  <Text variant="label-large">
                    {t('signup.welcome.languageLabel', 'Pick your language')}
                  </Text>
                </Box>
                <Box
                  role="radiogroup"
                  aria-label={t('signup.welcome.languageLabel', 'Pick your language')}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.25,
                  }}
                >
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <LanguageRow
                      key={lang}
                      native={LANGUAGE_META[lang].native}
                      english={LANGUAGE_META[lang].english}
                      selected={preferredLang === lang}
                      onSelect={() => onPickLanguage(lang)}
                    />
                  ))}
                </Box>
              </Box>

              <Button size="large" fullWidth onClick={() => setStep(0)}>
                {t('signup.welcome.continue', 'Continue')}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary">
                    {t('signup.footer.backToSignIn')}
                  </Typography>
                </Link>
              </Box>
            </>
          ) : (
            <>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 1 }}>
              <Text variant="display-small" as="h1" align="center">
                {t('signup.heading')}
              </Text>
            </Box>
            <Text variant="body-large" align="center" color="text.secondary">
              {t('signup.tagline')}
            </Text>
          </Box>

          <Stepper steps={[...STEPS]} activeStep={step} />

          <Card>
            <CardContent>
              {step === 0 && (
                <form onSubmit={onSubmitPhone} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('signup.phone.body')}
                    </Typography>
                    <PhoneInput
                      label={t('signup.phone.label')}
                      value={phone}
                      onChangeText={setPhone}
                      error={fieldErrors.phone}
                      disabled={pending}
                    />

                    {/* Privacy reassurance — a low-emphasis card explaining the
                        number is only used to log in (design fidelity). Tokens
                        only, so it recolours in dark mode. */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.5,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                      }}
                    >
                      <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>
                        <Icon name="shield" size={20} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'signup.phone.privacy',
                          'Your number is only used to log you in. We never share it with buyers.',
                        )}
                      </Typography>
                    </Box>

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending ? t('signup.phone.sending') : t('signup.phone.send')}
                    </Button>
                  </Stack>
                </form>
              )}

              {step === 1 && (
                <form onSubmit={onSubmitOtp} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('signup.verify.body', { phone: `+27${phone}` })}
                    </Typography>
                    <Box>
                      <OtpInput
                        value={code}
                        onChange={setCode}
                        length={5}
                        error={!!fieldErrors.code}
                        disabled={pending}
                        aria-label={t('signup.verify.label')}
                      />
                      {fieldErrors.code && (
                        <Box sx={{ mt: 1 }}>
                          <Text variant="body-small" color="error.main">
                            {fieldErrors.code}
                          </Text>
                        </Box>
                      )}
                    </Box>

                    {/* Resend code — a local countdown gates the action; at zero
                        it re-requests the OTP and restarts the timer. */}
                    <Box sx={{ textAlign: 'center' }}>
                      {resendSeconds > 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t('signup.verify.resendIn', {
                            seconds: `0:${String(resendSeconds).padStart(2, '0')}`,
                            defaultValue: 'Resend code in {{seconds}}',
                          })}
                        </Typography>
                      ) : (
                        <Button
                          variant="text"
                          onClick={onResendOtp}
                          disabled={resending || pending}
                        >
                          {resending
                            ? t('signup.verify.resending', 'Sending...')
                            : t('signup.verify.resend', 'Resend code')}
                        </Button>
                      )}
                    </Box>

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending ? t('signup.verify.verifying') : t('signup.verify.verify')}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={pending}
                      onClick={() => {
                        setApiErrorMessage(null);
                        setFieldErrors({});
                        setCode('');
                        setStep(0);
                      }}
                    >
                      {t('signup.verify.useDifferent')}
                    </Button>
                  </Stack>
                </form>
              )}

              {step === 2 && (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('signup.terms.intro')}
                  </Typography>

                  {consentLoading && (
                    <Typography variant="body2" color="text.secondary">
                      {t('signup.terms.loading')}
                    </Typography>
                  )}

                  {consentError && (
                    <Alert
                      severity="error"
                      variant="outlined"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => setConsentReload((n) => n + 1)}
                        >
                          {t('signup.terms.retry')}
                        </Button>
                      }
                    >
                      {consentError}
                    </Alert>
                  )}

                  {consent && !consentLoading && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {t('signup.terms.version', {
                          version: consent.version,
                          effective: consent.effective_from,
                        })}
                      </Typography>
                      <Box
                        ref={consentBoxRef}
                        onScroll={(e) => {
                          const el = e.currentTarget;
                          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
                            setScrolledToBottom(true);
                          }
                        }}
                        sx={{
                          maxHeight: 280,
                          overflowY: 'auto',
                          whiteSpace: 'pre-wrap',
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          fontSize: 14,
                        }}
                      >
                        {consent.body_md}
                      </Box>
                      {!scrolledToBottom && (
                        <Typography variant="caption" color="text.secondary">
                          {t('signup.terms.scrollHint')}
                        </Typography>
                      )}

                      {apiErrorMessage && (
                        <Alert severity="error" variant="outlined">
                          {apiErrorMessage}
                        </Alert>
                      )}

                      <Button
                        size="large"
                        fullWidth
                        disabled={pending || !scrolledToBottom}
                        onClick={onAcceptConsent}
                      >
                        {t('signup.terms.accept')}
                      </Button>
                    </>
                  )}
                </Stack>
              )}

              {step === 3 && (
                <form onSubmit={onSubmitProfile} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('signup.profile.body')}
                    </Typography>
                    <TextField
                      label={t('signup.profile.displayName')}
                      autoComplete="name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      slotProps={{ htmlInput: { maxLength: 200 } }}
                      error={!!fieldErrors.displayName}
                      helperText={fieldErrors.displayName}
                      disabled={pending}
                    />

                    <FormControl fullWidth disabled={pending}>
                      <InputLabel id="signup-language-label">{t('signup.profile.preferredLang')}</InputLabel>
                      <Select
                        labelId="signup-language-label"
                        id="signup-language"
                        value={preferredLang}
                        label={t('signup.profile.preferredLang')}
                        onChange={onLangChange}
                      >
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <MenuItem key={lang} value={lang}>
                            {LANGUAGE_LABELS[lang]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label={t('signup.profile.pin')}
                      type="password"
                      autoComplete="new-password"
                      inputMode="numeric"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      error={!!fieldErrors.pin}
                      helperText={fieldErrors.pin ?? t('signup.profile.pinHint')}
                      disabled={pending}
                    />
                    <TextField
                      label={t('signup.profile.confirmPin')}
                      type="password"
                      autoComplete="new-password"
                      inputMode="numeric"
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value)}
                      error={!!fieldErrors.confirmPin}
                      helperText={fieldErrors.confirmPin}
                      disabled={pending}
                    />

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending ? t('signup.profile.creating') : t('signup.profile.create')}
                    </Button>
                  </Stack>
                </form>
              )}
            </CardContent>
          </Card>

          <Box sx={{ textAlign: 'center' }}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" color="primary">
                {t('signup.footer.backToSignIn')}
              </Typography>
            </Link>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('signup.footer.claimPrompt')}{' '}
              <Link to="/claim" style={{ textDecoration: 'none' }}>
                <Typography component="span" variant="body2" color="primary">
                  {t('signup.footer.claimLink')}
                </Typography>
              </Link>
            </Typography>
          </Box>
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
